import {
  scrollToElement,
  removeUnitFromValue,
} from "../../../js/utils/helper/helper";

class Accordion {
  /**
   * @param {HTMLElement} element - the accordion element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        headlineSelector: ".js-Accordion-itemHeadline",
        innerContentSelector: ".js-Accordion-innerContent",
        buttonTemplateSelector: ".js-Accordion-buttonTemplate",
        buttonSelector: ".js-Accordion-button",
      },
      classes: {
        open: "is-open",
        cssButton: "Accordion-button",
      },
      customProperties: {
        transition: "--default-transition-time",
      },
    };

    this.headlines = [
      ...element.querySelectorAll(this.config.selectors.headlineSelector),
    ];

    this._handleMouseEvents = this._handleMouseEvents.bind(this);
    this._init();
  }

  /**
   * init
   *
   * @private
   */
  _init() {
    this._replaceContent();
    this._disableFocusOnAllLinks();
    this._bind();
  }

  /**
   * replaces the content of the headline with the content of the template
   * since there is no sense to have buttons on the page if js is disabled
   *
   * @private
   */
  _replaceContent() {
    this.headlines.forEach((headline) => {
      const template = headline.querySelector(
        this.config.selectors.buttonTemplateSelector
      );
      const clone = template.content.firstElementChild.cloneNode(true);
      headline.classList.remove(this.config.classes.cssButton);
      headline.replaceChildren(clone);
    });
  }

  /**
   * removes tabindex from all links inside the whole accordion
   * to prevent them get focus (with `Tab` key) when the all accordion contents are closed
   *
   * @private
   */
  _disableFocusOnAllLinks() {
    const links = this.element.getElementsByTagName("a");
    for (let i = 0; i < links.length; i += 1) {
      links[i].setAttribute("tabindex", "-1");
    }
  }

  /**
   * binds events
   *
   * @private
   */
  _bind() {
    this.buttons = [
      ...this.element.querySelectorAll(this.config.selectors.buttonSelector),
    ];

    this.buttons.forEach((button) => {
      button.addEventListener("click", this._handleMouseEvents);
    });
  }

  /**
   * handle mouse clicks
   *
   * @param {object} event
   * @private
   */
  _handleMouseEvents(event) {
    const { target } = event;
    const activeButton = this.element.querySelector(`[aria-expanded="true"]`);

    if (activeButton) {
      this._closeContent(activeButton);
    }

    if (activeButton !== target) {
      this._openContent(target);
      this._scrollToElement(target);
    }
  }

  /**
   * scrolls to element
   *
   * @param {HTMLElement} element - element which should be scrolled to
   * @private
   */
  _scrollToElement(element) {
    const transition = getComputedStyle(
      document.documentElement
    ).getPropertyValue(this.config.customProperties.transition);

    const timeout = removeUnitFromValue(transition);
    scrollToElement(element, "smooth", timeout);
  }

  /**
   * close content
   *
   * @param {HTMLElement} element - element which should be closed
   * @private
   */
  _closeContent(element) {
    element.setAttribute("aria-expanded", "false");
    element.blur(); // to remove focus styles
    this._toggleFocusOnLinks(element, { enableFocus: false });
    this._toggleOpenClassOnContent(element, { showContent: false });
  }

  /**
   * open content
   *
   * @param {HTMLElement} element - element which should be open
   * @private
   */
  _openContent(element) {
    element.setAttribute("aria-expanded", "true");
    this._setContentHeight(element);
    this._toggleFocusOnLinks(element, { enableFocus: true });
    this._toggleOpenClassOnContent(element, { showContent: true });
  }

  /**
   * toggles the `open` class on the `Accordion-content` to hide or show content
   *
   * @param {HTMLElement} element
   * @param {object} state
   * @param {boolean} state.showContent
   * @private
   */
  _toggleOpenClassOnContent(element, { showContent }) {
    this.element
      .querySelector(`#${element.getAttribute("aria-controls")}`)
      .classList.toggle(this.config.classes.open, showContent);
  }

  /**
   * sets `--Accordion-content-height` custom property to the `content` the same size as a height of the `innerContent`,
   * to be able to animate content via `transition: height` in css
   *
   * @param {HTMLElement} element
   * @private
   */
  _setContentHeight(element) {
    const childHeight = element.parentElement.nextElementSibling
      .querySelector(this.config.selectors.innerContentSelector)
      .getBoundingClientRect().height;
    element.parentElement.nextElementSibling.style.setProperty(
      "--Accordion-content-height",
      `${childHeight / 10}rem`
    );
  }

  /**
   * enable/disable focus on links
   * to prevent links get focused in the hidden elements (with `Tab` keyboard key)
   * by toggling tabindex=-1 on all links inside the sibling of the given element
   *
   * @param {HTMLElement} element
   * @param {object} state
   * @param {boolean} state.enableFocus - state
   * @private
   */
  _toggleFocusOnLinks(element, { enableFocus }) {
    const content = element.parentElement.nextElementSibling.querySelector(
      this.config.selectors.innerContentSelector
    );
    const links = content.getElementsByTagName("a");
    for (let i = 0; i < links.length; i += 1) {
      if (enableFocus) {
        links[i].removeAttribute("tabindex");
      } else {
        links[i].setAttribute("tabindex", "-1");
      }
    }
  }
}

export default Accordion;
